import { listenWorkspaceHistory, writeWorkspaceHistory } from './workspace-history.js';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import type { AuthenticatedUser, TenantMemberProfile } from '@livechat/shared';

import {
	bootstrapStatus,
	currentAccessToken,
	getMemberProfile,
	logout,
	onAccessTokenChanged,
	realtimeUrl,
	refreshSession,
	updateMemberProfile,
} from './api.js';
import { autoOfflineDelay } from './auto-offline.js';
import { AccountSetup } from './components/AccountSetup.js';
import { AuthScreen } from './components/AuthScreen.js';
import { GlobalNotifications } from './components/GlobalNotifications.js';
import { MembershipInvitation } from './components/MembershipInvitation.js';
import { PasswordReset } from './components/PasswordReset.js';
import { PlatformAiAgents } from './components/PlatformAiAgents.js';
import { PlatformAiUsage } from './components/PlatformAiUsage.js';
import { PlatformDashboard } from './components/PlatformDashboard.js';
import { PlatformMessageEnhance } from './components/PlatformMessageEnhance.js';
import { PlatformPlans } from './components/PlatformPlans.js';
import { PlatformSettings } from './components/PlatformSettings.js';
import { PlatformTenantDetails } from './components/PlatformTenantDetails.js';
import { PlatformTenants } from './components/PlatformTenants.js';
import { MemberAvatar } from './components/MemberAvatar.js';
import { ThemeSelector } from './components/ThemeSelector.js';
import { StatusPicker } from './components/StatusPicker.js';
import { reportNavigation, type ReportSection } from './components/TenantReports.js';
import { UiStandards } from './components/UiStandards.js';
import {
	TenantWorkspace,
	workspaceNavigation,
	workspaceNavigationItemIsActive,
	type WorkspaceTab,
} from './components/TenantWorkspace.js';
import { settingsNavigation, type SettingsSection } from './components/TenantSettingsWorkspace.js';
import { AiSettingsNavigation } from './components/AiSettingsNavigation.js';
import { activeMemberships, lastTenantKey, preferredTenant } from './tenant-selection.js';
import { storedThemeHint, watchThemePreference } from './theme.js';
import {
	membershipInvitationToken,
	parseWorkspaceLocation,
	workspaceHref,
} from './workspace-location.js';

interface SessionState {
	bootstrapRequired: boolean;
	loading: boolean;
	user?: AuthenticatedUser;
}

type OwnerPage =
	| 'ai-agents'
	| 'ai-usage'
	| 'customer'
	| 'customers'
	| 'dashboard'
	| 'message-enhance'
	| 'plans'
	| 'settings'
	| 'ui-standards';

export function App() {
	const setupToken = new URLSearchParams(window.location.search).get('setup');
	const passwordResetToken = new URLSearchParams(window.location.search).get('reset');
	const [membershipToken, setMembershipToken] = useState(() =>
		membershipInvitationToken(window.location),
	);
	const [notice, setNotice] = useState<string>();
	const [session, setSession] = useState<SessionState>({
		bootstrapRequired: false,
		loading: true,
	});
	const [selectedTenantId, setSelectedTenantId] = useState<string>();
	const [ownerPage, setOwnerPage] = useState<OwnerPage>('dashboard');
	const [ownerTenantId, setOwnerTenantId] = useState<string>();
	const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>('dashboard');
	const [settingsUserId, setSettingsUserId] = useState<string>();
	const [settingsDepartmentId, setSettingsDepartmentId] = useState<string>();
	const [knowledgeCollectionSlug, setKnowledgeCollectionSlug] = useState<string>();
	const [settingsSection, setSettingsSection] = useState<SettingsSection>('user');
	const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
	const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [reportSection, setReportSection] = useState<ReportSection>('agent');
	const [requestedConversationId, setRequestedConversationId] = useState<string>();
	const [claimRequestedConversation, setClaimRequestedConversation] = useState(false);
	const [activeConversationId, setActiveConversationId] = useState<string>();
	const [historyVisitorId, setHistoryVisitorId] = useState<string>();
	const [memberProfile, setMemberProfile] = useState<TenantMemberProfile>();
	const memberships = session.user ? activeMemberships(session.user) : [];
	const selectedMembership = session.user
		? (memberships.find((membership) => membership.tenantId === selectedTenantId) ??
			(!session.user.isPlatformAdmin && memberships.length === 1
				? memberships[0]
				: undefined))
		: undefined;

	const applyCurrentLocation = useCallback((user: AuthenticatedUser) => {
		setSidebarOpen(false);
		const invitationToken = membershipInvitationToken(window.location);
		setMembershipToken(invitationToken);
		if (invitationToken) return;
		const location = parseWorkspaceLocation(window.location);
		if (
			(location.kind === 'customer' ||
				location.kind === 'customers' ||
				location.kind === 'owner-ai-agents' ||
				location.kind === 'owner-ai-usage' ||
				location.kind === 'owner-dashboard' ||
				location.kind === 'owner-message-enhance' ||
				location.kind === 'owner-plans' ||
				location.kind === 'owner-settings' ||
				location.kind === 'owner-ui-standards') &&
			user.isPlatformAdmin
		) {
			setOwnerPage(
				location.kind === 'owner-ai-agents'
					? 'ai-agents'
					: location.kind === 'owner-ai-usage'
						? 'ai-usage'
						: location.kind === 'owner-dashboard'
							? 'dashboard'
							: location.kind === 'owner-message-enhance'
								? 'message-enhance'
								: location.kind === 'owner-plans'
									? 'plans'
									: location.kind === 'owner-settings'
										? 'settings'
										: location.kind === 'owner-ui-standards'
											? 'ui-standards'
											: location.kind === 'customer'
												? 'customer'
												: 'customers',
			);
			setOwnerTenantId(location.ownerTenantId);
			setSelectedTenantId(undefined);
			setWorkspaceTab('dashboard');
			setSettingsUserId(undefined);
			setSettingsDepartmentId(undefined);
			setSettingsSection('user');
			setSettingsMenuOpen(false);
			setReportsMenuOpen(false);
			setReportSection('agent');
			setRequestedConversationId(undefined);
			setClaimRequestedConversation(false);
			setHistoryVisitorId(undefined);
			return;
		}
		const availableMemberships = activeMemberships(user);
		const routedTenantId = availableMemberships.some(
			(membership) => membership.tenantId === location.tenantId,
		)
			? location.tenantId
			: undefined;
		const tenantId =
			location.kind === 'workspace' || !user.isPlatformAdmin
				? (routedTenantId ?? preferredTenant(user))
				: undefined;
		if (routedTenantId) {
			localStorage.setItem(lastTenantKey(user.id), routedTenantId);
		}
		const tab = location.kind === 'workspace' && location.tab ? location.tab : 'dashboard';
		setSelectedTenantId(tenantId);
		setOwnerTenantId(undefined);
		setWorkspaceTab(tab);
		setSettingsMenuOpen(tab === 'settings');
		setReportsMenuOpen(tab === 'reports');
		setReportSection(
			location.kind === 'workspace' ? (location.reportSection ?? 'agent') : 'agent',
		);
		setSettingsUserId(location.kind === 'workspace' ? location.settingsUserId : undefined);
		setSettingsDepartmentId(location.kind === 'workspace' ? location.departmentId : undefined);
		setKnowledgeCollectionSlug(
			location.kind === 'workspace' ? location.knowledgeCollectionSlug : undefined,
		);
		setSettingsSection(
			location.kind === 'workspace' ? (location.settingsSection ?? 'user') : 'user',
		);
		setRequestedConversationId(
			location.kind === 'workspace' ? location.conversationId : undefined,
		);
		setClaimRequestedConversation(
			location.kind === 'workspace' && location.claimConversation === true,
		);
		setHistoryVisitorId(location.kind === 'workspace' ? location.visitorId : undefined);
		if (tenantId || (location.kind === 'workspace' && !user.isPlatformAdmin)) {
			const canonicalHref = workspaceHref({
				claimConversation: location.claimConversation,
				conversationId: location.conversationId,
				departmentId: location.departmentId,
				knowledgeCollectionSlug: location.knowledgeCollectionSlug,
				reportSection: location.reportSection,
				settingsUserId: location.settingsUserId,
				settingsSection: location.settingsSection,
				tab,
				visitorId: location.visitorId,
			});
			if (`${window.location.pathname}${window.location.search}` !== canonicalHref) {
				writeWorkspaceHistory(canonicalHref, true);
			}
		} else if (user.isPlatformAdmin && window.location.pathname !== '/owner/dashboard') {
			setOwnerPage('dashboard');
			writeWorkspaceHistory('/owner/dashboard', true);
		}
	}, []);

	const navigateWorkspace = useCallback(
		({
			claimConversation = false,
			conversationId,
			departmentId,
			knowledgeCollectionSlug: targetKnowledgeCollectionSlug,
			reportSection: targetReportSection,
			replace = false,
			settingsUserId: targetSettingsUserId,
			settingsSection: targetSettingsSection,
			tab,
			tenantId,
			visitorId,
		}: {
			claimConversation?: boolean;
			conversationId?: string;
			departmentId?: string;
			knowledgeCollectionSlug?: string;
			reportSection?: ReportSection;
			replace?: boolean;
			settingsUserId?: string;
			settingsSection?: SettingsSection;
			tab: WorkspaceTab;
			tenantId?: string;
			visitorId?: string;
		}) => {
			setSidebarOpen(false);
			setSelectedTenantId(tenantId);
			setWorkspaceTab(tab);
			setSettingsMenuOpen(tab === 'settings');
			setReportsMenuOpen(tab === 'reports');
			setReportSection(tab === 'reports' ? (targetReportSection ?? 'agent') : 'agent');
			setSettingsUserId(tab === 'settings' ? targetSettingsUserId : undefined);
			setSettingsDepartmentId(
				tab === 'settings' && targetSettingsSection === 'departments'
					? departmentId
					: undefined,
			);
			setKnowledgeCollectionSlug(
				tab === 'settings' && targetSettingsSection === 'ai-knowledge'
					? targetKnowledgeCollectionSlug
					: undefined,
			);
			setSettingsSection(tab === 'settings' ? (targetSettingsSection ?? 'user') : 'user');
			setRequestedConversationId(tab === 'chats' ? conversationId : undefined);
			setClaimRequestedConversation(tab === 'chats' && claimConversation);
			setHistoryVisitorId(tab === 'history' ? visitorId : undefined);
			if (tab !== 'chats') setActiveConversationId(undefined);
			const href = workspaceHref({
				claimConversation,
				conversationId,
				departmentId,
				knowledgeCollectionSlug: targetKnowledgeCollectionSlug,
				reportSection: targetReportSection,
				settingsUserId: targetSettingsUserId,
				settingsSection: targetSettingsSection,
				tab,
				visitorId,
			});
			if (`${window.location.pathname}${window.location.search}` === href) return;
			writeWorkspaceHistory(href, replace);
		},
		[],
	);

	const openConversation = useCallback(
		(conversationId?: string, claimConversation = false) => {
			if (!selectedMembership) return;
			navigateWorkspace({
				claimConversation,
				conversationId,
				tab: 'chats',
				tenantId: selectedMembership.tenantId,
			});
		},
		[navigateWorkspace, selectedMembership],
	);

	useEffect(
		() => watchThemePreference(session.user?.themePreference ?? storedThemeHint()),
		[session.user?.themePreference],
	);

	useEffect(() => {
		if (!sidebarOpen) return;
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setSidebarOpen(false);
		};
		document.addEventListener('keydown', closeOnEscape);
		return () => document.removeEventListener('keydown', closeOnEscape);
	}, [sidebarOpen]);

	useEffect(() => {
		if (setupToken || passwordResetToken) {
			setSession((current) => ({ ...current, loading: false }));
			return;
		}

		void bootstrapStatus()
			.then(async (required) => {
				if (required) {
					setSession({ bootstrapRequired: true, loading: false });
					return;
				}
				try {
					const authentication = await refreshSession();
					setSession({
						bootstrapRequired: false,
						loading: false,
						user: authentication.user,
					});
					applyCurrentLocation(authentication.user);
				} catch {
					setSession({ bootstrapRequired: false, loading: false });
				}
			})
			.catch(() => setSession({ bootstrapRequired: false, loading: false }));
	}, [applyCurrentLocation, passwordResetToken, setupToken]);

	useEffect(() => {
		if (!session.user) return;
		return listenWorkspaceHistory(() => applyCurrentLocation(session.user!));
	}, [applyCurrentLocation, session.user]);

	useEffect(() => {
		if (!session.user || !selectedMembership) {
			setMemberProfile(undefined);
			return;
		}
		const userId = session.user.id;
		const tenantId = selectedMembership.tenantId;
		const refreshProfile = () =>
			void getMemberProfile(tenantId, userId)
				.then(setMemberProfile)
				.catch(() => undefined);
		refreshProfile();
		const token = currentAccessToken();
		if (!token) return;
		const socket = io(`${realtimeUrl}/chat`, {
			auth: { accessToken: token, tenantId },
			transports: ['websocket', 'polling'],
		});
		let lastRealtimeRefreshAt = 0;
		const stopWatchingToken = onAccessTokenChanged((updatedToken) => {
			socket.auth = { accessToken: updatedToken, tenantId };
			if (updatedToken && !socket.connected) socket.connect();
		});
		socket.on('connect', () => {
			refreshProfile();
		});
		socket.on('conversation:created', refreshProfile);
		socket.on('conversation:updated', refreshProfile);
		socket.on('session:error', () => {
			if (Date.now() - lastRealtimeRefreshAt < 60_000) return;
			lastRealtimeRefreshAt = Date.now();
			void refreshSession()
				.then((authentication) => {
					setSession((current) => ({ ...current, user: authentication.user }));
					socket.auth = { accessToken: authentication.accessToken, tenantId };
					socket.connect();
				})
				.catch(() => undefined);
		});
		socket.on('disconnect', () =>
			setMemberProfile((current) =>
				current ? { ...current, presenceStatus: 'offline' } : current,
			),
		);
		return () => {
			stopWatchingToken();
			socket.disconnect();
		};
	}, [selectedMembership?.tenantId, session.user?.id]);

	useEffect(() => {
		if (!session.user || !selectedMembership || !memberProfile) return;
		const delay = autoOfflineDelay(memberProfile.autoOfflineMinutes);
		if (delay === undefined || memberProfile.selectedPresence === 'offline') return;

		let active = true;
		let changingPresence = false;
		let timeoutId: number | undefined;
		let lastActivityAt = Date.now();
		const activityEvents = [
			'keydown',
			'pointerdown',
			'pointermove',
			'touchstart',
			'wheel',
		] as const;

		const armTimer = () => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(() => {
				if (changingPresence) return;
				changingPresence = true;
				void updateMemberProfile(selectedMembership.tenantId, session.user!.id, {
					selectedPresence: 'offline',
				})
					.then((profile) => {
						if (active) setMemberProfile(profile);
					})
					.catch(() => {
						changingPresence = false;
						if (active) armTimer();
					});
			}, delay);
		};
		const recordActivity = () => {
			const now = Date.now();
			if (now - lastActivityAt < 1_000) return;
			lastActivityAt = now;
			armTimer();
		};

		armTimer();
		activityEvents.forEach((eventName) =>
			document.addEventListener(eventName, recordActivity, { passive: true }),
		);
		return () => {
			active = false;
			window.clearTimeout(timeoutId);
			activityEvents.forEach((eventName) =>
				document.removeEventListener(eventName, recordActivity),
			);
		};
	}, [
		memberProfile?.autoOfflineMinutes,
		memberProfile?.selectedPresence,
		selectedMembership?.tenantId,
		session.user?.id,
	]);

	if (setupToken) {
		return (
			<AccountSetup
				token={setupToken}
				onComplete={() => {
					writeWorkspaceHistory(window.location.pathname, true);
					setNotice('Your account is ready. Sign in to continue.');
					setSession({ bootstrapRequired: false, loading: false });
				}}
			/>
		);
	}

	if (passwordResetToken) {
		return (
			<PasswordReset
				token={passwordResetToken}
				onComplete={() => {
					writeWorkspaceHistory(window.location.pathname, true);
					setNotice('Your password has been updated. Sign in to continue.');
					setSession({ bootstrapRequired: false, loading: false });
				}}
			/>
		);
	}

	if (session.loading) {
		return (
			<div className="loading-page">
				<span className="loading-mark" />
				<p>Opening your secure workspace…</p>
			</div>
		);
	}

	if (!session.user) {
		return (
			<AuthScreen
				bootstrapRequired={session.bootstrapRequired}
				notice={
					notice ??
					(membershipToken
						? 'Sign in to your existing account to accept the company invitation.'
						: undefined)
				}
				onAuthenticated={(user) => {
					setNotice(undefined);
					setSession({ bootstrapRequired: false, loading: false, user });
					applyCurrentLocation(user);
				}}
			/>
		);
	}

	if (membershipToken) {
		return (
			<MembershipInvitation
				key={membershipToken}
				token={membershipToken}
				onAccepted={(tenantId) => {
					void refreshSession().then((authentication) => {
						localStorage.setItem(lastTenantKey(authentication.user.id), tenantId);
						writeWorkspaceHistory(workspaceHref({ tab: 'dashboard' }), true);
						setMembershipToken(undefined);
						setSelectedTenantId(tenantId);
						setWorkspaceTab('dashboard');
						setSettingsMenuOpen(false);
						setSession({
							bootstrapRequired: false,
							loading: false,
							user: authentication.user,
						});
					});
				}}
			/>
		);
	}
	const isPlatformAdmin = session.user.isPlatformAdmin;

	const selectTenant = (tenantId: string | undefined) => {
		setSidebarOpen(false);
		if (tenantId) {
			localStorage.setItem(lastTenantKey(session.user!.id), tenantId);
			navigateWorkspace({ tab: 'dashboard', tenantId });
		} else {
			setOwnerPage('dashboard');
			setOwnerTenantId(undefined);
			setSelectedTenantId(undefined);
			setWorkspaceTab('dashboard');
			setSettingsUserId(undefined);
			setSettingsDepartmentId(undefined);
			setSettingsSection('user');
			setSettingsMenuOpen(false);
			setRequestedConversationId(undefined);
			setClaimRequestedConversation(false);
			setActiveConversationId(undefined);
			writeWorkspaceHistory('/owner/dashboard');
		}
	};

	const selectOwnerPage = (page: OwnerPage, tenantId?: string) => {
		setSidebarOpen(false);
		setOwnerPage(page);
		setOwnerTenantId(page === 'customer' ? tenantId : undefined);
		setSelectedTenantId(undefined);
		setWorkspaceTab('dashboard');
		setSettingsUserId(undefined);
		setSettingsDepartmentId(undefined);
		setSettingsSection('user');
		setSettingsMenuOpen(false);
		setRequestedConversationId(undefined);
		setClaimRequestedConversation(false);
		setActiveConversationId(undefined);
		setHistoryVisitorId(undefined);
		const href =
			page === 'dashboard'
				? '/owner/dashboard'
				: page === 'message-enhance'
					? '/owner/message-enhance'
					: page === 'plans'
						? '/owner/plans'
						: page === 'ai-agents'
							? '/owner/ai-agents'
							: page === 'ai-usage'
								? '/owner/ai-usage'
								: page === 'ui-standards'
									? '/owner/ui-standards'
									: page === 'settings'
										? '/owner/settings'
										: page === 'customer' && tenantId
											? `/customers/${encodeURIComponent(tenantId)}`
											: '/customers';
		if (window.location.pathname !== href) writeWorkspaceHistory(href);
	};

	return (
		<div className="dashboard-shell">
			<aside
				className={sidebarOpen ? 'sidebar responsive-open' : 'sidebar'}
				id="workspace-sidebar"
			>
				<a
					className="brand brand-light"
					href={
						selectedMembership
							? workspaceHref({
									tab: 'dashboard',
								})
							: '/owner/dashboard'
					}
					aria-label="Livechat home"
					onClick={(event) => {
						event.preventDefault();
						selectTenant(selectedMembership?.tenantId);
					}}
				>
					<img className="brand-icon" src="/livechat-icon.png" alt="" />
					<span>Livechat</span>
				</a>

				<nav aria-label="Workspace navigation">
					{session.user.isPlatformAdmin && (
						<div className="sidebar-company single-company">
							<div className="workspace-subnav">
								<button
									className={
										!selectedTenantId && ownerPage === 'dashboard'
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('dashboard')}
								>
									Dashboard
								</button>
								<button
									className={
										!selectedTenantId && ownerPage === 'plans' ? 'active' : ''
									}
									onClick={() => selectOwnerPage('plans')}
								>
									Plans
								</button>
								<button
									className={
										!selectedTenantId &&
										(ownerPage === 'customer' || ownerPage === 'customers')
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('customers')}
								>
									Customers
								</button>
								<button
									className={
										!selectedTenantId && ownerPage === 'message-enhance'
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('message-enhance')}
								>
									AI Message Enhance
								</button>
								<button
									className={
										!selectedTenantId && ownerPage === 'ai-agents'
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('ai-agents')}
								>
									AI Agents
								</button>
								<button
									className={
										!selectedTenantId && ownerPage === 'ai-usage'
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('ai-usage')}
								>
									AI Usage
								</button>
								<button
									className={
										!selectedTenantId && ownerPage === 'ui-standards'
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('ui-standards')}
								>
									UI Standards
								</button>
								<button
									className={
										!selectedTenantId && ownerPage === 'settings'
											? 'active'
											: ''
									}
									onClick={() => selectOwnerPage('settings')}
								>
									Settings
								</button>
							</div>
						</div>
					)}
					{memberships.map((membership) => {
						const selected = selectedMembership?.tenantId === membership.tenantId;
						const navigation = workspaceNavigation(membership, isPlatformAdmin);
						return (
							<div
								className={`sidebar-company ${memberships.length === 1 && !isPlatformAdmin ? 'single-company' : ''}`}
								key={membership.tenantId}
							>
								{(memberships.length > 1 || isPlatformAdmin) && (
									<button
										className={`company-nav-button ${selected ? 'active' : ''}`}
										onClick={() => selectTenant(membership.tenantId)}
									>
										<span className="nav-avatar">
											{membership.tenantName.slice(0, 1).toUpperCase()}
										</span>
										<span>
											<strong>{membership.tenantName}</strong>
											<small>{membership.role}</small>
										</span>
									</button>
								)}
								{selected && (
									<div className="workspace-subnav">
										{navigation.map((item) => (
											<Fragment key={item.tab}>
												<button
													aria-current={
														workspaceNavigationItemIsActive(
															item,
															workspaceTab,
														)
															? 'page'
															: undefined
													}
													aria-expanded={
														item.tab === 'settings'
															? settingsMenuOpen
															: item.tab === 'reports'
																? reportsMenuOpen
																: undefined
													}
													className={`${workspaceNavigationItemIsActive(item, workspaceTab) ? 'active' : ''} ${item.tab === 'settings' || item.tab === 'reports' ? 'settings-menu-toggle' : ''}`.trim()}
													onClick={() => {
														if (item.tab === 'settings') {
															setSettingsMenuOpen(
																(current) => !current,
															);
															return;
														}
														if (item.tab === 'reports') {
															setReportsMenuOpen(
																(current) => !current,
															);
															return;
														}
														navigateWorkspace({
															tab: item.tab,
															tenantId: membership.tenantId,
														});
													}}
												>
													<span>{item.label}</span>
													{(item.tab === 'settings' ||
														item.tab === 'reports') && (
														<svg
															aria-hidden="true"
															className="settings-menu-chevron"
															focusable="false"
															viewBox="0 0 20 20"
														>
															<path d="m6 8 4 4 4-4" />
														</svg>
													)}
												</button>
												{item.tab === 'settings' && settingsMenuOpen && (
													<div
														aria-label="Settings navigation"
														className="settings-sidebar-subnav"
														role="group"
													>
														{settingsNavigation(membership).map(
															(settingsItem) =>
																settingsItem.section === 'ai' ? (
																	<AiSettingsNavigation
																		key="ai"
																		activeSection={
																			workspaceTab ===
																			'settings'
																				? settingsSection
																				: undefined
																		}
																		onNavigate={(section) =>
																			navigateWorkspace({
																				settingsSection:
																					section,
																				tab: 'settings',
																				tenantId:
																					membership.tenantId,
																			})
																		}
																	/>
																) : (
																	<button
																		aria-current={
																			workspaceTab ===
																				'settings' &&
																			settingsSection ===
																				settingsItem.section
																				? 'page'
																				: undefined
																		}
																		className={
																			workspaceTab ===
																				'settings' &&
																			settingsSection ===
																				settingsItem.section
																				? 'active'
																				: ''
																		}
																		key={settingsItem.section}
																		onClick={() => {
																			navigateWorkspace({
																				settingsSection:
																					settingsItem.section,
																				tab: 'settings',
																				tenantId:
																					membership.tenantId,
																			});
																		}}
																		type="button"
																	>
																		{settingsItem.label}
																	</button>
																),
														)}
													</div>
												)}
												{item.tab === 'reports' && reportsMenuOpen && (
													<div
														aria-label="Reports navigation"
														className="settings-sidebar-subnav"
														role="group"
													>
														{reportNavigation(membership).map(
															(reportItem) => (
																<button
																	aria-current={
																		workspaceTab ===
																			'reports' &&
																		reportSection ===
																			reportItem.section
																			? 'page'
																			: undefined
																	}
																	className={
																		workspaceTab ===
																			'reports' &&
																		reportSection ===
																			reportItem.section
																			? 'active'
																			: ''
																	}
																	key={reportItem.section}
																	onClick={() =>
																		navigateWorkspace({
																			reportSection:
																				reportItem.section,
																			tab: 'reports',
																			tenantId:
																				membership.tenantId,
																		})
																	}
																	type="button"
																>
																	{reportItem.label}
																</button>
															),
														)}
													</div>
												)}
											</Fragment>
										))}
									</div>
								)}
							</div>
						);
					})}
				</nav>
				<button
					aria-label="Sign out"
					className="sidebar-logout"
					onClick={() =>
						void logout().finally(() => {
							writeWorkspaceHistory('/', true);
							setSession({ bootstrapRequired: false, loading: false });
						})
					}
				>
					<span>↗</span> Sign out
				</button>
			</aside>
			{sidebarOpen && (
				<button
					aria-label="Hide navigation"
					className="sidebar-responsive-backdrop"
					onClick={() => setSidebarOpen(false)}
					type="button"
				/>
			)}
			<button
				aria-controls="workspace-sidebar"
				aria-expanded={sidebarOpen}
				aria-label={sidebarOpen ? 'Hide navigation' : 'Show navigation'}
				className="sidebar-responsive-toggle"
				onClick={() => setSidebarOpen((current) => !current)}
				type="button"
			>
				<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20">
					<path d="m7 4 6 6-6 6" />
				</svg>
			</button>

			<main className="dashboard-main">
				{selectedMembership && memberProfile && (
					<GlobalNotifications
						membership={selectedMembership}
						onOpenConversation={openConversation}
						profile={memberProfile}
						suppressConversationId={
							workspaceTab === 'chats' ? activeConversationId : undefined
						}
					/>
				)}
				<header className="dashboard-topbar">
					<div className="topbar-company">
						{selectedMembership ? (
							<>
								{memberProfile && (
									<MemberAvatar
										dataUrl={
											memberProfile.avatarKind === 'uploaded'
												? memberProfile.avatarDataUrl
												: undefined
										}
										displayName={memberProfile.displayName}
										enabled
										preset={memberProfile.avatarPreset}
									/>
								)}
								<div>
									<strong>{selectedMembership.tenantName}</strong>
									<span>
										{memberProfile?.displayName ?? session.user.displayName}
									</span>
								</div>
							</>
						) : (
							<>
								<span className="system-indicator" />
								{ownerPage === 'dashboard'
									? 'Owner dashboard'
									: ownerPage === 'message-enhance'
										? 'AI Message Enhance'
										: ownerPage === 'plans'
											? 'Plans'
											: ownerPage === 'ai-agents'
												? 'AI Agents'
												: ownerPage === 'ai-usage'
													? 'AI Usage'
													: ownerPage === 'ui-standards'
														? 'UI Standards'
														: ownerPage === 'settings'
															? 'Owner settings'
															: ownerPage === 'customer'
																? 'Customer details'
																: 'Customer administration'}
							</>
						)}
					</div>
					<div className="topbar-actions">
						<ThemeSelector
							onChanged={(user) => setSession((current) => ({ ...current, user }))}
							preference={session.user.themePreference}
						/>
						{selectedMembership && memberProfile && (
							<StatusPicker
								onChange={(selectedPresence) =>
									void updateMemberProfile(
										selectedMembership.tenantId,
										session.user!.id,
										{ selectedPresence },
									).then(setMemberProfile)
								}
								value={memberProfile.presenceStatus}
							/>
						)}
					</div>
				</header>

				{session.user.isPlatformAdmin && !selectedTenantId ? (
					ownerPage === 'dashboard' ? (
						<PlatformDashboard />
					) : ownerPage === 'plans' ? (
						<PlatformPlans />
					) : ownerPage === 'message-enhance' ? (
						<PlatformMessageEnhance />
					) : ownerPage === 'ai-agents' ? (
						<PlatformAiAgents />
					) : ownerPage === 'ai-usage' ? (
						<PlatformAiUsage />
					) : ownerPage === 'ui-standards' ? (
						<UiStandards />
					) : ownerPage === 'settings' ? (
						<PlatformSettings />
					) : ownerPage === 'customer' && ownerTenantId ? (
						<PlatformTenantDetails
							onBack={() => selectOwnerPage('customers')}
							tenantId={ownerTenantId}
						/>
					) : (
						<PlatformTenants
							onSelectTenant={(tenantId) => selectOwnerPage('customer', tenantId)}
						/>
					)
				) : selectedMembership ? (
					<TenantWorkspace
						claimInitialConversation={claimRequestedConversation}
						currentUserId={session.user.id}
						isPlatformAdmin={isPlatformAdmin}
						initialConversationId={requestedConversationId}
						historyVisitorId={historyVisitorId}
						membership={selectedMembership}
						onActiveConversationChanged={setActiveConversationId}
						onConversationSelected={openConversation}
						onDepartmentSelected={(departmentId, replace = false) => {
							navigateWorkspace({
								departmentId,
								replace,
								settingsSection: 'departments',
								tab: 'settings',
								tenantId: selectedMembership.tenantId,
							});
						}}
						onNavigate={(tab) =>
							navigateWorkspace({ tab, tenantId: selectedMembership.tenantId })
						}
						onOpenMemberSettings={(userId) => {
							navigateWorkspace({
								settingsUserId: userId,
								tab: 'settings',
								tenantId: selectedMembership.tenantId,
							});
						}}
						onViewVisitorHistory={(visitorId) => {
							navigateWorkspace({
								tab: 'history',
								tenantId: selectedMembership.tenantId,
								visitorId,
							});
						}}
						onProfileChanged={setMemberProfile}
						reportSection={reportSection}
						onMemberDeleted={() =>
							navigateWorkspace({
								tab: 'team',
								tenantId: selectedMembership.tenantId,
							})
						}
						settingsDepartmentId={settingsDepartmentId}
						knowledgeCollectionSlug={knowledgeCollectionSlug}
						onKnowledgeCollectionSelected={(slug, replace = false) =>
							navigateWorkspace({
								knowledgeCollectionSlug: slug,
								replace,
								settingsSection: 'ai-knowledge',
								tab: 'settings',
								tenantId: selectedMembership.tenantId,
							})
						}
						settingsUserId={settingsUserId}
						settingsSection={settingsSection}
						tab={workspaceTab}
					/>
				) : memberships.length > 1 ? (
					<section className="workspace-section company-chooser">
						<div className="section-heading">
							<div>
								<p className="eyebrow">Your companies</p>
								<h2>Choose a company</h2>
								<p className="muted">
									Your role and permissions are separate in each company.
								</p>
							</div>
						</div>
						<div className="company-grid">
							{memberships.map((membership) => (
								<button
									className="company-card"
									key={membership.tenantId}
									onClick={() => selectTenant(membership.tenantId)}
								>
									<span className="nav-avatar">
										{membership.tenantName.slice(0, 1).toUpperCase()}
									</span>
									<strong>{membership.tenantName}</strong>
									<small className="capitalise">{membership.role}</small>
								</button>
							))}
						</div>
					</section>
				) : (
					<section className="workspace-section">
						<div className="empty-workspace">
							<h2>No active workspace</h2>
							<p>
								Your administrator needs to activate a tenant membership for this
								account.
							</p>
						</div>
					</section>
				)}
			</main>
		</div>
	);
}
